// gameengine/gameEvents.js

/**
 * THEME-AGNOSTIC EVENT GENERATION ENGINE
 * 
 * This module generates raw event data from provided event pools.
 * It contains NO theme-specific content, narratives, or descriptions.
 * Theme flavor is applied separately via applyThemeToEvent().
 */

/**
 * Generates scheduled events based on day patterns
 * @param {number} day - Current day number
 * @param {Array} scheduledEventPool - Pool of scheduled events from theme
 * @returns {Array} Array of triggered scheduled events (raw data)
 */
export const generateScheduledEvents = (day, scheduledEventPool = []) => {
  const events = [];

  for (const eventTemplate of scheduledEventPool) {
    let shouldTrigger = false;

    // Parse schedule pattern
    switch (eventTemplate.schedulePattern) {
      case 'every_7_days':
        shouldTrigger = day % 7 === 0;
        break;
      case 'every_5_days':
        shouldTrigger = day % 5 === 0;
        break;
      case 'every_10_days':
        shouldTrigger = day % 10 === 0;
        break;
      case 'every_3_days':
        shouldTrigger = day % 3 === 0;
        break;
      default:
        // Support custom patterns like "every_X_days"
        const match = eventTemplate.schedulePattern?.match(/every_(\d+)_days/);
        if (match) {
          const interval = parseInt(match[1], 10);
          shouldTrigger = day % interval === 0;
        }
    }

    if (shouldTrigger) {
      events.push({
        type: eventTemplate.id,
        category: eventTemplate.category,
        rewards: eventTemplate.rewards || {},
        cost: eventTemplate.cost || {},
        bonus: eventTemplate.bonus || false,
        threat: eventTemplate.threat || false,
        tags: eventTemplate.tags || [],
        storyHooks: eventTemplate.storyHooks || []
      });
    }
  }

  return events;
};

/**
 * Generates backdrop/atmospheric events
 * @param {number} day - Current day number
 * @param {Array} backdropEventPool - Pool of backdrop events from theme
 * @param {number} probability - Chance of backdrop event (0-1), default 0.3
 * @returns {Array} Array of triggered backdrop events (raw data)
 */
export const generateBackdropEvents = (day, backdropEventPool = [], probability = 0.3) => {
  const events = [];

  // Roll for a single backdrop event per day
  if (Math.random() < probability && backdropEventPool.length > 0) {
    const randomIndex = Math.floor(Math.random() * backdropEventPool.length);
    const eventTemplate = backdropEventPool[randomIndex];

    events.push({
      type: eventTemplate.id,
      category: eventTemplate.category,
      rewards: eventTemplate.rewards || {},
      cost: eventTemplate.cost || {},
      bonus: eventTemplate.bonus || false,
      threat: eventTemplate.threat || false,
      tags: eventTemplate.tags || [],
      storyHooks: eventTemplate.storyHooks || []
    });
  }

  return events;
};

/**
 * Generates threat events based on threat level
 * @param {number} threatLevel - Current threat level (1-10)
 * @param {Array} threatEventPool - Pool of threat events from theme
 * @returns {Array} Array of triggered threat events (raw data)
 */
export const generateThreatEvents = (threatLevel = 3, threatEventPool = []) => {
  const events = [];

  // Calculate threat probability based on threat level
  const baseProbability = threatLevel * 0.05; // 5% per threat level
  const roll = Math.random();

  if (roll < baseProbability && threatEventPool.length > 0) {
    // Filter events by threat intensity
    const availableEvents = threatEventPool.filter(e => {
      if (threatLevel <= 3) return e.threatIntensity === 'low';
      if (threatLevel <= 6) return e.threatIntensity === 'low' || e.threatIntensity === 'medium';
      return true; // All intensities available at high threat
    });

    if (availableEvents.length > 0) {
      const randomIndex = Math.floor(Math.random() * availableEvents.length);
      const eventTemplate = availableEvents[randomIndex];

      events.push({
        type: eventTemplate.id,
        category: eventTemplate.category,
        rewards: eventTemplate.rewards || {},
        cost: eventTemplate.cost || {},
        bonus: eventTemplate.bonus || false,
        threat: eventTemplate.threat || false,
        threatIntensity: eventTemplate.threatIntensity,
        tags: eventTemplate.tags || [],
        storyHooks: eventTemplate.storyHooks || []
      });
    }
  }

  return events;
};

/**
 * Generates morale events based on recent performance
 * @param {Object} gameState - Current game state (squad status, recent victories, etc.)
 * @param {Array} moraleEventPool - Pool of morale events from theme
 * @returns {Array} Array of triggered morale events (raw data)
 */
export const generateMoraleEvents = (gameState = {}, moraleEventPool = []) => {
  const events = [];

  // Check for morale triggers
  const recentVictories = gameState.recentVictories || 0;
  
  // Morale boost probability increases with victories
  const moraleProbability = recentVictories > 0 ? 0.4 : 0.1;
  
  if (Math.random() < moraleProbability && moraleEventPool.length > 0) {
    // Filter by morale type based on context
    let availableEvents = moraleEventPool;
    
    if (recentVictories >= 2) {
      availableEvents = moraleEventPool.filter(e => 
        e.moraleType === 'major_victory' || e.moraleType === 'major_inspiration'
      );
    } else if (recentVictories === 1) {
      availableEvents = moraleEventPool.filter(e => 
        e.moraleType === 'minor_victory'
      );
    }

    if (availableEvents.length === 0) {
      availableEvents = moraleEventPool; // Fallback
    }

    const randomIndex = Math.floor(Math.random() * availableEvents.length);
    const eventTemplate = availableEvents[randomIndex];

    events.push({
      type: eventTemplate.id,
      category: eventTemplate.category,
      rewards: eventTemplate.rewards || {},
      cost: eventTemplate.cost || {},
      bonus: eventTemplate.bonus || false,
      threat: eventTemplate.threat || false,
      moraleType: eventTemplate.moraleType,
      tags: eventTemplate.tags || [],
      storyHooks: eventTemplate.storyHooks || []
    });
  }

  return events;
};

/**
 * Generates faction events
 * @param {Object} gameState - Current game state
 * @param {Array} factionEventPool - Pool of faction events from theme
 * @returns {Array} Array of triggered faction events (raw data)
 */
export const generateFactionEvents = (gameState = {}, factionEventPool = []) => {
  const events = [];

  // Faction events are less frequent
  const factionProbability = 0.15;

  if (Math.random() < factionProbability && factionEventPool.length > 0) {
    const randomIndex = Math.floor(Math.random() * factionEventPool.length);
    const eventTemplate = factionEventPool[randomIndex];

    events.push({
      type: eventTemplate.id,
      category: eventTemplate.category,
      rewards: eventTemplate.rewards || {},
      cost: eventTemplate.cost || {},
      bonus: eventTemplate.bonus || false,
      threat: eventTemplate.threat || false,
      tags: eventTemplate.tags || [],
      storyHooks: eventTemplate.storyHooks || []
    });
  }

  return events;
};

/**
 * Generates discovery events
 * @param {Object} gameState - Current game state
 * @param {Array} discoveryEventPool - Pool of discovery events from theme
 * @returns {Array} Array of triggered discovery events (raw data)
 */
export const generateDiscoveryEvents = (gameState = {}, discoveryEventPool = []) => {
  const events = [];

  // Discovery events are rare but impactful
  const discoveryProbability = 0.1;

  if (Math.random() < discoveryProbability && discoveryEventPool.length > 0) {
    const randomIndex = Math.floor(Math.random() * discoveryEventPool.length);
    const eventTemplate = discoveryEventPool[randomIndex];

    events.push({
      type: eventTemplate.id,
      category: eventTemplate.category,
      rewards: eventTemplate.rewards || {},
      cost: eventTemplate.cost || {},
      bonus: eventTemplate.bonus || false,
      threat: eventTemplate.threat || false,
      tags: eventTemplate.tags || [],
      storyHooks: eventTemplate.storyHooks || []
    });
  }

  return events;
};

/**
 * Generates campaign events (major turning points)
 * @param {Object} gameState - Current game state
 * @param {Array} campaignEventPool - Pool of campaign events from theme
 * @returns {Array} Array of triggered campaign events (raw data)
 */
export const generateCampaignEvents = (gameState = {}, campaignEventPool = []) => {
  const events = [];

  // Campaign events are very rare and triggered by specific conditions
  const day = gameState.campaign?.day || 1;
  const threatLevel = gameState.campaign?.threat_level || 3;

  // Only trigger at major milestones
  const isMilestone = day % 30 === 0 || threatLevel >= 8;
  const campaignProbability = isMilestone ? 0.3 : 0.05;

  if (Math.random() < campaignProbability && campaignEventPool.length > 0) {
    const randomIndex = Math.floor(Math.random() * campaignEventPool.length);
    const eventTemplate = campaignEventPool[randomIndex];

    events.push({
      type: eventTemplate.id,
      category: eventTemplate.category,
      rewards: eventTemplate.rewards || {},
      cost: eventTemplate.cost || {},
      bonus: eventTemplate.bonus || false,
      threat: eventTemplate.threat || false,
      tags: eventTemplate.tags || [],
      storyHooks: eventTemplate.storyHooks || []
    });
  }

  return events;
};

/**
 * MAIN EVENT GENERATOR
 * Consolidates all event generation for a given day
 * 
 * @param {number} day - Current day number
 * @param {Object} gameState - Current game state
 * @param {Object} eventLibrary - Theme event library with all event pools
 * @returns {Array} Array of all triggered events (raw data, no flavor)
 */
export const generateDayEvents = (day, gameState = {}, eventLibrary = {}) => {
  const allEvents = [];

  // Extract event pools from library
  const {
    SCHEDULED_EVENTS = [],
    BACKDROP_EVENTS = [],
    THREAT_EVENTS = [],
    MORALE_EVENTS = [],
    FACTION_EVENTS = [],
    DISCOVERY_EVENTS = [],
    CAMPAIGN_EVENTS = []
  } = eventLibrary;

  // Generate events by category
  const scheduledEvents = generateScheduledEvents(day, SCHEDULED_EVENTS);
  const backdropEvents = generateBackdropEvents(day, BACKDROP_EVENTS);
  const threatEvents = generateThreatEvents(
    gameState.campaign?.threat_level || 3,
    THREAT_EVENTS
  );
  const moraleEvents = generateMoraleEvents(gameState, MORALE_EVENTS);
  const factionEvents = generateFactionEvents(gameState, FACTION_EVENTS);
  const discoveryEvents = generateDiscoveryEvents(gameState, DISCOVERY_EVENTS);
  const campaignEvents = generateCampaignEvents(gameState, CAMPAIGN_EVENTS);

  // Combine all events
  allEvents.push(
    ...scheduledEvents,
    ...backdropEvents,
    ...threatEvents,
    ...moraleEvents,
    ...factionEvents,
    ...discoveryEvents,
    ...campaignEvents
  );

  return allEvents;
};

/**
 * Legacy compatibility function
 * Generates a random event (now uses threat events as default)
 * @param {number} threatLevel - Current threat level
 * @param {Array} threatEventPool - Pool of threat events
 * @returns {Object|null} Random event or null
 */
export const generateRandomEvent = (threatLevel, threatEventPool = []) => {
  const events = generateThreatEvents(threatLevel, threatEventPool);
  return events.length > 0 ? events[0] : null;
};